import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Video, Loader2, CheckSquare, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { VideoFrameDetailDialog } from "./VideoFrameDetailDialog";
import { useFileUpload } from "@/hooks/useFileUpload";
import { toast } from "sonner";

interface VideoAnalysisSectionProps {
  jobId: string;
  orgId: string;
}

interface FrameAnalysis {
  id: string;
  timestamp: number;
  imageUrl: string;
  status: 'pending' | 'analyzing' | 'complete' | 'error';
    analysis?: {
      damageDetected: boolean;
      predictions: Array<{
        x: number;
        y: number;
        width: number;
        height: number;
        confidence: number;
        class: string;
        bounding_box?: number[];
      }>;
      findings: string[];
      summary?: {
        totalFindings: number;
        hailCount: number;
        otherDamageCount: number;
      };
    };
  error?: string;
}

export const VideoAnalysisSection = ({ jobId, orgId }: VideoAnalysisSectionProps) => {
  const videoInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile } = useFileUpload(orgId);
  const [frames, setFrames] = useState<FrameAnalysis[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedFrames, setSelectedFrames] = useState<Set<string>>(new Set());
  const [detailFrame, setDetailFrame] = useState<FrameAnalysis | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [addingToProposal, setAddingToProposal] = useState(false);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const validTypes = ["video/mp4", "video/quicktime", "video/x-msvideo"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a valid video file (MP4, MOV, or AVI)");
        return;
      }

      if (file.size > 500 * 1024 * 1024) {
        toast.error("Video file must be less than 500MB");
        return;
      }

      // Check video duration before processing
      const videoDuration = await getVideoDuration(file);
      const MAX_DURATION = 300; // 5 minutes
      
      if (videoDuration > MAX_DURATION) {
        const durationMinutes = Math.round(videoDuration / 60);
        toast.error(`Video too long (${durationMinutes} min). Maximum is 5 minutes. Please trim your video.`);
        return;
      }

      toast.success(`Video duration: ${Math.round(videoDuration / 60)} min ${Math.round(videoDuration % 60)} sec`);

      setExtracting(true);
      const extracted = await extractFramesFromVideo(file);
      setFrames(extracted);
    } catch (error) {
      console.error("Error extracting frames:", error);
      toast.error(error instanceof Error ? error.message : "Failed to process video. Please try a different file.");
    } finally {
      setExtracting(false);
      event.target.value = "";
    }
  };

  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.src = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        if (!isFinite(video.duration) || video.duration <= 0) {
          reject(new Error("Could not read video duration"));
          return;
        }
        resolve(video.duration);
      };
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error("Failed to load video"));
      };
    });
  };

  const extractFramesFromVideo = (file: File): Promise<FrameAnalysis[]> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.src = URL.createObjectURL(file);
      video.crossOrigin = "anonymous";
      video.muted = true;
      video.playsInline = true;

      video.onloadedmetadata = async () => {
        const duration = video.duration;
        if (!isFinite(duration) || duration <= 0) {
          reject(new Error("Could not read video duration"));
          return;
        }

        // 1 frame per 5 seconds, max 60 frames
        const calculatedFrames = Math.floor(duration / 5);
        const frameCount = Math.max(6, Math.min(60, calculatedFrames));
        const times: number[] = [];
        const step = duration / (frameCount + 1);
        for (let i = 1; i <= frameCount; i++) {
          times.push(step * i);
        }

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not create canvas context"));
          return;
        }

        const results: FrameAnalysis[] = [];

        const captureAtTime = (time: number): Promise<void> => {
          return new Promise((resolveCapture, rejectCapture) => {
            const onSeeked = () => {
              // Resize to max 1280px for AI analysis
              const maxWidth = 1280;
              const scale = Math.min(1, maxWidth / video.videoWidth);
              canvas.width = Math.round(video.videoWidth * scale);
              canvas.height = Math.round(video.videoHeight * scale);
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const dataUrl = canvas.toDataURL("image/jpeg", 0.8);

              results.push({
                id: `${time.toFixed(2)}-${results.length}`,
                timestamp: time,
                imageUrl: dataUrl,
                status: "pending",
              });

              video.removeEventListener("seeked", onSeeked);
              resolveCapture();
            };

            video.addEventListener("seeked", onSeeked);
            video.currentTime = time;
          });
        };

        try {
          for (const t of times) {
            await captureAtTime(t);
          }
          URL.revokeObjectURL(video.src);
          resolve(results);
        } catch (err) {
          URL.revokeObjectURL(video.src);
          reject(err);
        }
      };

      video.onerror = () => {
        reject(new Error("Failed to load video"));
      };
    });
  };

  const analyzeFrames = async () => {
    if (!frames.length || analyzing) return;

    setAnalyzing(true);
    try {
      const updatedFrames: FrameAnalysis[] = [];
      const totalFrames = frames.length;

      for (let i = 0; i < frames.length; i++) {
        const frame = frames[i];
        const frameNumber = i + 1;
        
        // Show progress in toast
        toast.info(`Analyzing frame ${frameNumber} of ${totalFrames}...`, { duration: 2000 });
        
        const baseFrame: FrameAnalysis = { ...frame };
        updatedFrames.push({ ...baseFrame, status: "analyzing" });
        setFrames([...updatedFrames, ...frames.slice(updatedFrames.length)]);

        try {
          console.log(`Analyzing frame ${frameNumber}/${totalFrames} at ${frame.timestamp.toFixed(1)}s`);
          const { data, error } = await supabase.functions.invoke("analyze-damage", {
            body: {
              image: frame.imageUrl,
              context: `Video frame ${frameNumber}/${totalFrames} at ${frame.timestamp.toFixed(1)}s from job ${jobId}`,
              source: "VIDEO",
              orgId: orgId,
            },
          });

          if (error) {
            console.error(`Frame ${frameNumber} analysis error:`, error);
            const errorMsg = typeof error === "string" ? error : error?.message || "Analysis failed";
            updatedFrames[updatedFrames.length - 1] = {
              ...baseFrame,
              status: "error",
              error: errorMsg,
            };
            toast.error(`Frame ${frameNumber} failed: ${errorMsg}`);
          } else {
             console.log(`Frame ${frameNumber} analyzed:`, data);
             updatedFrames[updatedFrames.length - 1] = {
               ...baseFrame,
               status: "complete",
               analysis: data?.damage || data,
             };
           }
        } catch (err) {
          console.error(`Error analyzing frame ${frameNumber}:`, err);
          const errorMsg = err instanceof Error ? err.message : "Unknown error";
          updatedFrames[updatedFrames.length - 1] = {
            ...baseFrame,
            status: "error",
            error: errorMsg,
          };
          toast.error(`Frame ${frameNumber} error: ${errorMsg}`);
        }
      }

      setFrames(updatedFrames);
      const successCount = updatedFrames.filter(f => f.status === "complete").length;
      const errorCount = updatedFrames.filter(f => f.status === "error").length;
      toast.success(`Analysis complete: ${successCount} successful, ${errorCount} failed`);
    } catch (error) {
      console.error("Error analyzing frames:", error);
      toast.error("Failed to analyze frames");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFrameClick = (frame: FrameAnalysis) => {
    setDetailFrame(frame);
    setDetailDialogOpen(true);
  };

  const handleToggleFrameSelection = (frameId: string) => {
    setSelectedFrames(prev => {
      const newSet = new Set(prev);
      if (newSet.has(frameId)) {
        newSet.delete(frameId);
      } else {
        newSet.add(frameId);
      }
      return newSet;
    });
  };

  const handleAddToProposal = async (frame: FrameAnalysis) => {
    if (!frame.analysis?.damageDetected) {
      toast.error("No damage detected in this frame");
      return;
    }

    try {
      setAddingToProposal(true);

      const response = await fetch(frame.imageUrl);
      const blob = await response.blob();
      const file = new File([blob], `video-frame-${frame.timestamp}s.jpg`, { type: 'image/jpeg' });

      const uploadResult = await uploadFile(file, {
        bucket: 'job-photos',
        folder: jobId,
      });

      if (!uploadResult) throw new Error('Upload failed');

      const { error: dbError } = await supabase.from("photos").insert({
        job_id: jobId,
        org_id: orgId,
        filename: `Video Frame ${frame.timestamp.toFixed(1)}s`,
        storage_path: uploadResult.path,
        url: uploadResult.url,
        ai_analyzed: true,
        ai_damage_detected: true,
        ai_damage_types: frame.analysis.predictions.map(p => p.class),
        ai_analysis_result: frame.analysis,
      });

      if (dbError) throw dbError;

      toast.success("Frame added to proposal photos");
    } catch (error) {
      console.error("Error adding frame to proposal:", error);
      toast.error("Failed to add frame to proposal");
    } finally {
      setAddingToProposal(false);
    }
  };

  const handleAddSelectedToProposal = async () => {
    const framesToAdd = frames.filter(f => selectedFrames.has(f.id) && f.analysis?.damageDetected);
    
    if (framesToAdd.length === 0) {
      toast.error("No frames with damage selected");
      return;
    }

    try {
      setAddingToProposal(true);
      
      for (const frame of framesToAdd) {
        await handleAddToProposal(frame);
      }

      setSelectedFrames(new Set());
      toast.success(`${framesToAdd.length} frame(s) added to proposal`);
    } catch (error) {
      console.error("Error adding frames:", error);
      toast.error("Failed to add some frames");
    } finally {
      setAddingToProposal(false);
    }
  };

  const completedCount = frames.filter((f) => f.status === "complete").length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Video className="h-4 w-4" />
            Video Damage Analysis (Real Frames)
          </span>
          <div className="flex items-center gap-2">
            <input
              id="video-upload-input"
              type="file"
              accept="video/mp4,video/quicktime,video/x-msvideo"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button
              size="sm"
              asChild
              disabled={extracting || analyzing}
            >
              <label htmlFor="video-upload-input" className="flex items-center cursor-pointer">
                {extracting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Video
                  </>
                )}
              </label>
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!frames.length ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No frames captured yet</p>
            <p className="text-xs mt-1">
              Upload a video (max 5 minutes, 500MB) and we will extract frames for damage analysis.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                <div>
                  {frames.length} frames extracted (1 per 5s) • {completedCount}/{frames.length} analyzed
                  {frames.filter(f => f.status === "error").length > 0 && (
                    <span className="text-destructive ml-1">
                      • {frames.filter(f => f.status === "error").length} failed
                    </span>
                  )}
                </div>
                {selectedFrames.size > 0 && (
                  <div className="text-primary mt-1">
                    {selectedFrames.size} frame{selectedFrames.size > 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {selectedFrames.size > 0 && (
                  <Button
                    size="sm"
                    onClick={handleAddSelectedToProposal}
                    disabled={addingToProposal}
                    variant="secondary"
                  >
                    {addingToProposal ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <CheckSquare className="h-4 w-4 mr-2" />
                        Add to Proposal
                      </>
                    )}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={analyzeFrames}
                  disabled={analyzing}
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyzing
                    </>
                  ) : (
                    <>Run AI Analysis</>
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>Overall Progress</span>
                <span>
                  {completedCount} / {frames.length} frames analyzed
                </span>
              </div>
              <Progress
                value={frames.length ? (completedCount / frames.length) * 100 : 0}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {frames.map((frame) => {
                const damageTypes = frame.analysis?.predictions.reduce((acc, pred) => {
                  acc[pred.class] = (acc[pred.class] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>);

                return (
                  <div
                    key={frame.id}
                    className={`border rounded-md overflow-hidden bg-muted/40 flex flex-col cursor-pointer transition-all hover:shadow-lg ${
                      selectedFrames.has(frame.id) ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => handleFrameClick(frame)}
                  >
                    <div className="relative aspect-video bg-black/80 group">
                      <img
                        src={frame.imageUrl}
                        alt={`Frame at ${frame.timestamp.toFixed(1)} seconds`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute top-1 left-1">
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {frame.timestamp.toFixed(1)}s
                        </Badge>
                      </div>
                      <div 
                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFrameSelection(frame.id);
                        }}
                      >
                        <Checkbox
                          checked={selectedFrames.has(frame.id)}
                          onCheckedChange={() => handleToggleFrameSelection(frame.id)}
                          className="bg-white"
                        />
                      </div>
                      {frame.analysis?.damageDetected && (
                        <Badge className="absolute bottom-1 right-1 bg-destructive text-destructive-foreground text-[10px] px-1.5 py-0">
                          Damage
                        </Badge>
                      )}
                      {frame.analysis?.predictions && frame.analysis.predictions.length > 0 && (
                        <div className="absolute bottom-1 left-1 flex gap-1">
                          {Object.entries(damageTypes || {}).slice(0, 3).map(([type]) => (
                            <div
                              key={type}
                              className="w-2 h-2 rounded-full"
                              style={{
                                backgroundColor: type === 'hail-damage' ? '#ef4444' : 
                                                type === 'wind-damage' ? '#f59e0b' :
                                                type === 'missing-shingles' ? '#8b5cf6' : '#6b7280'
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="p-2 border-t bg-background/60 flex-1 flex flex-col">
                      {frame.status === "complete" && frame.analysis ? (
                        <div className="text-[10px] leading-tight text-muted-foreground space-y-1">
                          {Object.entries(damageTypes || {}).slice(0, 2).map(([type, count]) => (
                            <div key={type} className="flex items-center gap-1">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{
                                  backgroundColor: type === 'hail-damage' ? '#ef4444' : 
                                                  type === 'wind-damage' ? '#f59e0b' :
                                                  type === 'missing-shingles' ? '#8b5cf6' : '#6b7280'
                                }}
                              />
                              <span className="capitalize">{type.replace('-', ' ')}: {count}</span>
                            </div>
                          ))}
                          {Object.keys(damageTypes || {}).length > 2 && (
                            <div>+{Object.keys(damageTypes || {}).length - 2} more...</div>
                          )}
                        </div>
                      ) : frame.status === "error" && frame.error ? (
                        <div className="flex items-center gap-1 text-[10px] text-destructive">
                          <AlertCircle className="w-3 h-3" />
                          <span className="line-clamp-2">{frame.error}</span>
                        </div>
                      ) : (
                        <div className="text-[10px] text-muted-foreground">
                          {frame.status === "pending"
                            ? "Ready for analysis"
                            : frame.status === "analyzing"
                            ? "Analyzing..."
                            : ""}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <VideoFrameDetailDialog
              open={detailDialogOpen}
              onOpenChange={setDetailDialogOpen}
              frame={detailFrame}
              onAddToProposal={handleAddToProposal}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
